import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Taskbar } from '@/components/desktop/Taskbar';
import { AutoBreadcrumbs } from './AutoBreadcrumbs';
import { PinBar } from './PinnedPages';
import { DemoBanner } from '@/components/common/DemoBanner';
import { FloatingRequestButton } from '@/components/dev/FloatingRequestButton';
import { SidebarProvider } from '@/lib/context/SidebarContext';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { useWindowed } from '@/lib/context/WindowManagerContext';

interface MainLayoutProps {
  children: ReactNode;
  taskBankContent?: ReactNode;
  clientListPanel?: ReactNode;
  catalogMenuPanel?: ReactNode;
  noPadding?: boolean;
  aboveBreadcrumbs?: ReactNode;
}

const TRACKED_PREFIXES = ['/employees', '/clients', '/crews', '/jobs'];

function saveLastPath(pathname: string) {
  for (const prefix of TRACKED_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      localStorage.setItem(`lastNav_${prefix}`, pathname);
      return;
    }
  }
}

export const MainLayout = ({
  children,
  taskBankContent,
  clientListPanel,
  catalogMenuPanel,
  noPadding = false,
  aboveBreadcrumbs,
}: MainLayoutProps) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  // Inside a desktop window: no taskbar/mobile chrome, no ⌘K handler, no
  // lastNav tracking — the window shows only the page content.
  const windowed = useWindowed();

  useEffect(() => {
    if (!windowed) saveLastPath(location.pathname);
  }, [location.pathname, windowed]);

  useEffect(() => {
    if (windowed) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(v => !v);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [windowed]);

  const isCalendarPage  = location.pathname.startsWith('/calendar');
  const isClientsPage   = location.pathname.startsWith('/clients');
  const isEmployeesPage = location.pathname.startsWith('/employees');
  const isCrewsPage     = location.pathname.startsWith('/crews');
  const isJobsPage      = location.pathname.startsWith('/jobs');
  const isCatalogPage   = location.pathname.startsWith('/catalog');
  const isDocumentsPage = location.pathname.startsWith('/documents');

  const contextPanel = (() => {
    if ((isClientsPage || isEmployeesPage || isCrewsPage || isJobsPage || isDocumentsPage) && clientListPanel)
      return clientListPanel;
    if (isCatalogPage && catalogMenuPanel) return catalogMenuPanel;
    if (isCalendarPage && taskBankContent) return taskBankContent;
    return null;
  })();

  // Windowed mode — content only (with its context panel); the window frame
  // supplies the chrome. Never render the taskbar or mobile bars in a window.
  if (windowed) {
    return (
      <SidebarProvider>
        <div className="flex h-full w-full overflow-hidden">
          {contextPanel && (
            <aside className="hidden md:flex flex-col w-64 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
              {contextPanel}
            </aside>
          )}
          <main
            className={`flex-1 min-w-0 overflow-y-auto ${
              noPadding ? '' : 'px-1.5 py-3 md:p-6 flex flex-col gap-4 md:gap-6'
            }`}
          >
            {children}
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex flex-col h-screen w-full overflow-hidden">
        <DemoBanner />
        {/* Desktop taskbar (replaces the old top nav) */}
        <Taskbar onSearchOpen={() => setSearchOpen(true)} />

        {/* Mobile header */}
        <Header onSearchOpen={() => setSearchOpen(true)} />

        {/* Mobile sidebar drawer — main menu only (page sections are accordions on the page) */}
        <Sidebar />

        {/* Slot rendered above the breadcrumbs (#73 — dev tools bar) */}
        {aboveBreadcrumbs}

        {/* Site-wide file-tree breadcrumbs (Clients / <client> / Jobs / <job> / …) */}
        <AutoBreadcrumbs />
        <PinBar />

        {/* Content row */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Context panel — desktop only, rendered as inline left panel */}
          {contextPanel && (
            <aside className="hidden md:flex flex-col w-64 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
              {contextPanel}
            </aside>
          )}

          <main
            className={`flex-1 min-w-0 overflow-y-auto pb-[env(safe-area-inset-bottom)] md:pb-0 ${
              noPadding ? '' : 'px-1.5 py-3 md:p-6 flex flex-col gap-4 md:gap-6'
            }`}
          >
            {children}
          </main>
        </div>
        {/* Bottom tab bar removed for the admin app (2026-07-21) — mobile navigates
            via the header hamburger → sidebar drawer. The client/employee portals
            keep their own bottom nav (src/portal/PortalLayout.tsx). */}

      </div>

      <FloatingRequestButton />
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    </SidebarProvider>
  );
};
